// src/data/services/api.js
import axios from 'axios';

const HOST = process.env.REACT_APP_PAS_API_HOST;
const DEFAULT_PORT = process.env.REACT_APP_PAS_API_PORT;

const authService = {
  getTokens: () => {
    const storage = JSON.parse(localStorage.getItem('persist:root') || '{}');
    const authState = JSON.parse(storage.auth || '{}');
    return {
      accessToken: authState.user?.accessToken || '',
      refreshToken: authState.user?.refreshToken || '',
      path: authState.pasPort,
    };
  },
};

export const getApiBaseUrl = () => {

  const { path } = authService.getTokens();

  if(process.env.NODE_ENV === 'production' && window.location.hostname?.includes('mobilesongpagongdanpark2.or.kr')) {
    return `${HOST}:${DEFAULT_PORT}/pas${path || 8021}/v2/`;
  }else {
    return `${HOST}:${DEFAULT_PORT}/v2/`;
  }
  
};

const apiConfig = {
  baseURL: getApiBaseUrl(),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};

const api = axios.create({
  baseURL: apiConfig.baseURL,
  headers: apiConfig.headers,
});

// Variable to hold the logout function and refresh handler (will be injected later)
let logoutHandler = null;
let refreshHandler = null;

// Function to inject the logout handler from the React app
export const injectLogoutHandler = (handler) => {
  logoutHandler = handler;
};

// Function to inject the refresh handler from the React app
export const injectRefreshHandler = (handler) => {
  refreshHandler = handler;
};

//Gardens to save the request of the request for waiting
let pendingRequests = [];

// The function calls back the requests waiting when the refresh is complete
const resolvePendingRequests = (accessToken) => {
  pendingRequests.forEach(({ resolve, config }) => {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
    resolve(axios(config));
  });
  pendingRequests = [];
};

//The reject function of the requests waits when the refresh fails
const rejectPendingRequests = (error) => {
  pendingRequests.forEach(({ reject }) => reject(error));
  pendingRequests = [];
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;

const formatResponse = (response) => {
  if (typeof response.data === 'string') {
    return response.data;
  } else if (typeof response.data === 'object' && response.data !== null && !Array.isArray(response.data)) {
    return {
      ...response.data,
      status: response.status,
    };
  }
  return response.data;
};

const handleApiError = async (error) => {
  const status = error.response?.status;

  if (status === 401) {
    console.warn('Unauthorized, attempting to refresh token...');

    // If already refreshing, wait and retry the original request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject, config: error.config });
      });
    }

    // Attempt to refresh the token
    isRefreshing = true;
    try {
      const { refreshToken } = authService.getTokens();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Call the refresh handler to update tokens

      const accessToken = await refreshHandler(refreshToken);


      // Retry the original request with the new Access Token
      // const { accessToken } = authService.getTokens();
      if (accessToken) {
        resolvePendingRequests(accessToken);
        error.config.headers['Authorization'] = `Bearer ${accessToken}`;

        const response = await axios(error.config);

        return formatResponse(response);
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (refreshError) {
      console.warn('Token refresh failed:', refreshError.message);
      rejectPendingRequests(refreshError);
      // If refresh fails, trigger logout
      if (logoutHandler) {
        logoutHandler();
      }
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }

  switch (status) {
    case 400:
      console.error('Bad Request:', error.response.data);
      break;
    case 403:
      console.warn('승인되지 않았습니다.')
      break;
    case 405:
      console.warn('승인되지 않았습니다.')
      break;
    case 440:
      console.warn('세션이 만료되었습니다. 다시 로그인 해주세요.');
      if (logoutHandler) {
        logoutHandler();
      }
      break;
    case 500:
      console.warn('승인되지 않았습니다.')
      break;
    default:
      console.error('Unknow error', error.message);
  }
  return Promise.reject(error);
};

api.interceptors.request.use(
  (config) => {
    const { accessToken } = authService.getTokens();
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    // format the response data
    return formatResponse(response);
  },
  (error) => handleApiError(error)
);

export default api;